import classNames from "classnames";
import { Header } from "components/atoms/Typography";
import { IncreaseDecreasePercentage } from "components/molecules/Card/CardFunctions";
import ChartContainer from "components/molecules/ChartContainer";
import { BarChart } from "components/molecules/Charts";
import errorFallbackUI from "components/molecules/ErrorFallbackUI.tsx/ErrorFallbackUI";
import { ComponentErrorBoundary } from "components/molecules/ErrorStates";
import { ChartNoDataState } from "components/molecules/NoDataState";
import { Table } from "components/molecules/Table";
import { MARGIN } from "components/organisms/Charts/AnnualCashFlowChart.constants";
import { DATA_KEY_COLORS } from "constants/chartsColors.constant";
import { line } from "d3-shape";
import { useMemo, useState } from "react";
import usePlansState from "recoilStore/Building/usePlansState";
import { formatRatioAsPercentage, getDisplayInfo } from "utils/formatting";
import {
    cashFlowChartBarOutcomesKeys,
    cashFlowTableOutcomesColumns,
    prepareAnnualCashFlowOutcomesData,
} from "./AnnualCashFlowOutcomes.helper";

// eslint-disable-next-line
interface AnnualCashFlowChartOutcomesProps<T> {
    height?: number;
    backgroundColor?: string;
}

function AnnualCashFlowOutcomesChart<T extends { [key: keyof any]: number }>({
    height = 187,
    backgroundColor = "white",
}: AnnualCashFlowChartOutcomesProps<T>) {
    const [showChart, setShowChart] = useState(true);
    const { referencePlan, comparedPlan, referencePlanName, comparedPlanName, comparingToBaseline } = usePlansState();
    const { chartData, incrementalRoiReference, roiEvolution, tooltipElement } = prepareAnnualCashFlowOutcomesData(
        referencePlan,
        comparedPlan,
        referencePlanName,
        comparedPlanName
    );

    const barData = useMemo(() => {
        if (!chartData || chartData?.length === 0) return [];

        return chartData.map((datum: any) => {
            const object: { [key: string]: number } = {};

            cashFlowChartBarOutcomesKeys.forEach((key) => {
                const { humanReadable } = getDisplayInfo(key);

                const value =
                    key.toLowerCase().includes("cost") && !key.toLowerCase().includes("saving")
                        ? datum[key] * -1
                        : datum[key];
                object[humanReadable] = value;
            });
            return {
                ...object,
                "Net cash flow": datum.cashFlowAnnual,
                label: datum.year || datum.calendarYear,
            };
        });
    }, [chartData]);

    const chartLabels = useMemo(
        () => cashFlowChartBarOutcomesKeys.map((key) => getDisplayInfo(key).humanReadable),
        [cashFlowChartBarOutcomesKeys]
    );

    const maxValue = useMemo(() => {
        if (barData.length === 0) return 0;
        const allValues = barData.flatMap((datum: any) => {
            // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
            const { label, "Net cash flow": netCashFlow, ...remainder } = datum;
            return Object.values(remainder) as number[];
        });
        return Math.max(...allValues);
    }, [barData]);

    const minValue = useMemo(() => {
        if (barData.length === 0) return 0;
        const allValues = barData.flatMap((datum: any) => {
            // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
            const { label, "Net cash flow": netCashFlow, ...remainder } = datum;
            return Object.values(remainder) as number[];
        });
        return Math.min(...allValues);
    }, [barData]);

    const { formatFunction, unit } = getDisplayInfo(
        "cashFlowCumulative",
        barData.flatMap((datum: any) => Object.values(datum))
    );

    const columns = cashFlowTableOutcomesColumns.map((col) => {
        const values = chartData?.map((d: any) => d[col]);
        const { unit, formatFunction, humanReadable } =
            chartData && chartData?.length > 0 && typeof chartData[0][col] === "number"
                ? getDisplayInfo(col, values)
                : getDisplayInfo(col);

        return {
            render: formatFunction,
            title: `${humanReadable} ${unit && `(${unit})`}`,
            key: col,
        };
    });

    const chartColorsMappedToLabels = (() => {
        const chartColorMap = new Map();
        cashFlowChartBarOutcomesKeys.forEach((key) => {
            const { humanReadable } = getDisplayInfo(key);
            chartColorMap.set(humanReadable, DATA_KEY_COLORS[key]);
        });

        return chartLabels
            .filter(
                (label) =>
                    label !== getDisplayInfo("calendarYear").humanReadable &&
                    label !== getDisplayInfo("year").humanReadable
            )
            .map((label) => ({
                color: chartColorMap.get(label),
                label,
            }));
    })();

    if (chartData === null) return <ChartNoDataState title="Cash flows" />;

    return (
        <div className={classNames("card", "card-with-chart-container")}>
            <ComponentErrorBoundary fallback={errorFallbackUI("cardHeaderError")} originComponent="Card">
                <Header>Cash Flows</Header>
                <div className="card__value-and-percentage">
                    <Header>{formatRatioAsPercentage(incrementalRoiReference)}% IRR</Header>
                    {!comparingToBaseline && (
                        <IncreaseDecreasePercentage
                            isHeader={true}
                            value={Number(formatRatioAsPercentage(roiEvolution))}
                            tooltipElement={tooltipElement}
                            invertedColor={true}
                        />
                    )}
                </div>
                <ChartContainer
                    title="Cash flows"
                    onClickTable={() => setShowChart(!showChart)}
                    exportableData={chartData}
                    hideTitle={true}
                >
                    {showChart ? (
                        <BarChart
                            data={barData}
                            backgroundColor={backgroundColor}
                            chartHeight={height}
                            includeLegend={true}
                            chartKeys={chartLabels}
                            margin={MARGIN}
                            chartColorsMappedToKeys={[
                                ...chartColorsMappedToLabels,
                                {
                                    label: "Net cash flow",
                                    color: DATA_KEY_COLORS.netCashFlow,
                                },
                            ]}
                            valueScale={{
                                type: "linear",
                                max: maxValue,
                                min: minValue,
                            }}
                            customLayers={[
                                // eslint-disable-next-line react/no-unstable-nested-components
                                (props) => <LineLayer {...props} indexByKey={chartLabels[0]} />,
                            ]}
                            yAxisInfo={{
                                formatFunction: (v) => formatFunction(v, false),
                                label: unit || "",
                            }}
                            xAxisInfo={{}}
                        />
                    ) : (
                        <div
                            style={{
                                maxHeight: "530px",
                                width: "100%",
                                overflow: "auto",
                            }}
                        >
                            <Table
                                dataSource={
                                    chartData?.map((datum: any) => ({
                                        key: datum.year,
                                        ...datum,
                                    })) || []
                                }
                                pagination={false}
                                columns={columns}
                            />
                        </div>
                    )}
                </ChartContainer>
            </ComponentErrorBoundary>
        </div>
    );
}

function LineLayer({ bars, xScale, yScale, indexByKey }: any) {
    const filteredBars = bars.filter((bar: any) => bar.key.includes(indexByKey));

    const lineGenerator = line()
        .x((bar: any) => xScale(bar.data.indexValue) + bar.width / 2)
        .y((bar: any) => yScale(bar.data.data["Net cash flow"] || 0));

    return (
        <path
            d={lineGenerator(filteredBars) || undefined}
            fill="none"
            stroke={DATA_KEY_COLORS.netCashFlow}
            style={{ pointerEvents: "none" }}
            strokeWidth={1}
        />
    );
}

export default AnnualCashFlowOutcomesChart;
